import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../services/apiClient";
import SearchIcon from "@mui/icons-material/Search";

import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";

import { setModal } from "../features/modal/ModalSlice";
import toast from "react-hot-toast";

const AiAgent = () => {
  const [aiAgents, setAIAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const modal = useSelector((state) => state?.modal?.modal);
  const userInfo = useSelector((state) => state?.userInfo?.user);
  // console.log('userInfo', userInfo)
  useEffect(() => {
    if (modal?.isOpen === false) {
      fetchAIAgent();
    }
  }, [modal]);

  //  useEffect(() => {

  //   fetchAIAgent();
  // }, []);
  const dispatch = useDispatch();

  const fetchAIAgent = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/agent-details-users/${userInfo?.id}`);
      setAIAgents(response?.data || []);
      // console.log(response.data, "------------------");
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleAddAgent = () => {
    dispatch(setModal({ type: "ADD_AI_AGENT" }));
  };

  const handleEditRequest = (request) => {
    dispatch(setModal({ type: "EDIT_AI_AGENT", data: request }));
  };

  const handleDeleteRequest = async (id) => {
    try {
      const response = await apiClient.delete(`/agent-details/${id}`);
      toast.success("Deleted !");
      fetchAIAgent();
    } catch (err) {
      console.log(err);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "agent_name", header: "Agent Name" },
      {
        accessorKey: "assigned_extension",
        header: "Extension Assigned",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "assigned_number",
        header: "Number Assigned",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "assigned_voice_type",
        header: "Voice",
        Cell: ({ row }) => <span>{row.original.assigned_voice_type.gender}</span>,
      },
      { accessorKey: "voice_source", header: "Voice Source" },
      {
        accessorKey: "assigned_language_type",
        header: "Language",
        Cell: ({ row }) => <span>{row.original.assigned_language_type.language_name}</span>,
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => <span>{row.original.status ? "Active" : "Inactive"}</span>,
      },
      {
        accessorKey: "created_at",
        header: "Date",
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
      {
        header: "Voice Sample",
        Cell: ({ row }) => {
          const sampleVoice = row?.original?.sample_voice;
          const audioUrl = sampleVoice
            ? `https://dialgrid-api.greymatterz.com/${sampleVoice.replace(/\\/g, "/")}`
            : null;

          return sampleVoice ? (
            <audio controls style={{ height: "30px" }}>
              <source src={audioUrl} type="audio/mpeg" />
              Your browser does not support audio playback.
            </audio>
          ) : (
            "-"
          );
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: aiAgents,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton onClick={() => handleEditRequest(row?.original)}>
          <RiEdit2Fill />
        </IconButton>
        <IconButton onClick={() => handleDeleteRequest(row?.original?._id)}>
          <MdDelete />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      <div className="">
        {loading && <PageLoader text="Loading Ai Agents" />}

        <div className="pageTitle1 p-5">
          <h3>Ai Agents</h3>
          <p>{aiAgents?.length} Available</p>
        </div>

        <Box sx={{ padding: 2 }}>
          <div className="d-flex justify-content-between">
            <div className="w-50">
              <CustomInputBox
                icon={SearchIcon}
                placeholder="Search Request"
                type="text"
                backgroundColor={"#FFFFFF"}
                borderRadius="8px"
              />
            </div>

            <div className="d-flex justify-content-end gap-3 mb-4">
              <CustomButton
                text={(showFilter ? "Hide" : "Show") + " Filter"}
                color="secondary"
                iconPosition="left"
                icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
                onClick={handleShowFilter}
              />

              <CustomButton text={"Create Agent"} onClick={handleAddAgent} />
            </div>
          </div>

          {showFilter && (
            <div className="d-flex justify-content-start gap-3 mb-4">
              <Autocomplete
                disablePortal
                options={[]}
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                size="small"
                renderInput={(params) => <TextField {...params} label="Name" />}
              />

              <Autocomplete
                disablePortal
                options={[]}
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                size="small"
                renderInput={(params) => <TextField {...params} label="Number" />}
              />

              <CustomButton text={"Reset"} variant="text" color="inherit" />
            </div>
          )}

          <MaterialReactTable table={table} />
        </Box>
      </div>
    </>
  );
};

export default AiAgent;
